import React, { FunctionComponent } from 'react';
import { IconButton, Tooltip, Typography } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FileCopy as FileCopyIcon,
} from '@mui/icons-material';

import { useAppSelector } from '../../../../../../hooks';
import { getAuthState } from '../../../../../../store/AuthSlice';
import { checkUserPermission } from '../../../../../../helper';
import { IStudentFlexibleRate } from '../../../../../../store/V2/StudentFlexibleRatesSlice';
import { IFlexiblePlan } from '../../../../../../store/V2/FlexiblePlansSlice';
import { IStudentFlexibleRateVersion } from '../../../../../../store/V2/StudentFlexibleRateVersionsSlice';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../components/V2/Table/type1/TableStyles';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';
import { getTenantsState } from '../../../../../../store/V2/TenantsSlice';

interface StudentFlexibleRateTableRowProps {
  studentFlexibleRate: IStudentFlexibleRate;
  onUpdate: () => void;
  onDelete: () => void;
  onView: () => void;
  onDuplicate: () => void;
}

const StudentFlexibleRateTableRow: FunctionComponent<StudentFlexibleRateTableRowProps> = ({
  studentFlexibleRate,
  onUpdate,
  onDelete,
  onView,
  onDuplicate,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  const packageData = studentFlexibleRate.package as IFlexiblePlan;
  const amountsData = studentFlexibleRate.amounts || [];
  const createPermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminSettings: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_CREATE,
      ],
    },
  });

  const updatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminSettings: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_EDIT,
      ],
    },
  });

  const deletePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminSettings: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_STUDENT_RATES_DELETE,
      ],
    },
  });

  let amountsText = '';

  if (amountsData.length) {
    amountsData.forEach((item) => {
      const studentFlexibleRateVersionData = item.studentFlexibleRateVersion as IStudentFlexibleRateVersion;

      amountsText += `, ${studentFlexibleRateVersionData.name} - ${item.currency} ${item.amount}`;
    });

    amountsText = amountsText.substring(1).trim();
  } else {
    amountsText = '-';
  }

  return (
    <StyledTableRow hover key={studentFlexibleRate._id}>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {packageData.name}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {studentFlexibleRate.country || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {studentFlexibleRate.curriculum || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {studentFlexibleRate.grade || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {amountsText}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Tooltip title="View">
          <IconButton onClick={onView}>
            <VisibilityIcon sx={{ color: '#1E293B' }} />
          </IconButton>
        </Tooltip>
        {updatePermission ? (
          <Tooltip title="Edit">
            <IconButton onClick={onUpdate}>
              <EditIcon sx={{ color: '#1E293B' }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {createPermission ? (
          <Tooltip title="Duplicate">
            <IconButton onClick={onDuplicate}>
              <FileCopyIcon sx={{ color: '#1E293B' }} />
            </IconButton>
          </Tooltip>
        ) : null}
        {deletePermission ? (
          <Tooltip title="Delete">
            <IconButton onClick={onDelete}>
              <DeleteIcon sx={{ color: '#1E293B' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default StudentFlexibleRateTableRow;
